import { loadChunkWithRetry } from './load-chunk-with-retry.js';

// Each route names its lazy chunk and the mount function that chunk exports.
// A route chunk name starts with react- (webpack/react-chunks.js). The build fails otherwise.
export const REACT_ROUTES = {
    album: { load: () => import(/* webpackChunkName: "react-album" */ '../react/album/index'), mount: 'mountAlbum' },
    'album-edit': { load: () => import(/* webpackChunkName: "react-album-edit" */ '../react/album-edit/index'), mount: 'mountAlbumEdit' },
    albums: { load: () => import(/* webpackChunkName: "react-albums" */ '../react/albums/index'), mount: 'mountAlbums' },
    bookmarks: { load: () => import(/* webpackChunkName: "react-bookmarks" */ '../react/bookmarks/index'), mount: 'mountBookmarks' },
    friends: { load: () => import(/* webpackChunkName: "react-friends" */ '../react/friends/index'), mount: 'mountFriends' },
    views: { load: () => import(/* webpackChunkName: "react-views" */ '../react/views/index'), mount: 'mountViews' },
    langex: { load: () => import(/* webpackChunkName: "react-langex" */ '../react/langex/index'), mount: 'mountLangex' },
    settings: { load: () => import(/* webpackChunkName: "react-settings" */ '../react/settings/index'), mount: 'mountSettings' },
    accountActivity: { load: () => import(/* webpackChunkName: "react-account-activity" */ '../react/account-activity/index'), mount: 'mountAccountActivity' },
    posts: { load: () => import(/* webpackChunkName: "react-posts" */ '../react/posts/index'), mount: 'mountPosts' },
};

const mountedPages = new WeakMap();
const missingRoute = () => Promise.resolve({});

/**
 * Mounts the React route named by the page's data-react-page into the server-rendered page and returns a disposer.
 *
 * The page element contains the mount root (with its skeleton), the failure panel,
 * and the JSON page data (components/react-page.twig). Its [data-react-slot] elements reach the route as options.slots.
 */
export function mountReactPage(page, load) {
    if (mountedPages.has(page)) return mountedPages.get(page);

    const route = page.dataset.reactPage;
    const config = Object.prototype.hasOwnProperty.call(REACT_ROUTES, route) ? REACT_ROUTES[route] : null;
    const root = page.querySelector('[data-react-root]');
    const failure = page.querySelector('[data-react-failure]');
    const dataScript = page.querySelector('[data-react-data]');
    if (!root || !failure || !dataScript) return () => {};
    const loadChunk = load || (config ? config.load : missingRoute);
    const mountExport = config ? config.mount : null;

    let disposed = false;
    let failed = false;
    let disposeReact;

    const clearWatchdog = () => {
        clearTimeout(Number(page.dataset.reactWatchdog));
        delete page.dataset.reactWatchdog;
    };
    const releaseReact = () => {
        const release = disposeReact;
        disposeReact = undefined;
        if (release) release();
    };
    const report = (error, phase) => {
        window.Sentry?.captureException?.(error, {
            tags: { react_route: route, react_phase: phase },
        });
    };
    const fail = (phase) => (error) => {
        if (disposed || failed) return;
        failed = true;
        report(error, phase);
        root.hidden = true;
        // Placeholders outside the root, such as the Views Edit slot, leave with it.
        page.querySelectorAll('[data-react-skeleton]').forEach((placeholder) => { placeholder.hidden = true; });
        failure.hidden = false;
        queueMicrotask(releaseReact);
    };
    const onCommit = () => {
        if (disposed || failed) return;
        root.removeAttribute('aria-busy');
    };
    const dispose = () => {
        if (disposed) return;
        disposed = true;
        releaseReact();
        window.removeEventListener('pagehide', onPageHide);
        mountedPages.delete(page);
    };
    const onPageHide = (event) => {
        if (!event.persisted) dispose();
    };

    // The watchdog covers a bundle that never runs. From here the chunk and render failure paths own the panel.
    clearWatchdog();
    root.hidden = false;
    failure.hidden = true;
    mountedPages.set(page, dispose);
    window.addEventListener('pagehide', onPageHide);

    loadChunkWithRetry(loadChunk).then((module) => {
        if (disposed || failed) return;
        if (!mountExport || typeof module?.[mountExport] !== 'function') {
            fail('chunk')(new Error(`React route "${route}" has no mount export`));
            return;
        }
        let data;
        try {
            data = JSON.parse(dataScript.textContent);
        } catch (error) {
            fail('render')(error);
            return;
        }
        const slots = {};
        page.querySelectorAll('[data-react-slot]').forEach((slot) => { slots[slot.dataset.reactSlot] = slot; });
        disposeReact = module[mountExport](root, data, {
            slots,
            onCommit,
            onError: fail('render'),
            // React recovers from these errors itself, so they never reveal the panel.
            onRecoverableError: (error) => report(error, 'recoverable'),
        });
        if (failed) queueMicrotask(releaseReact);
    }, fail('chunk'));

    return dispose;
}

function start() {
    document.querySelectorAll('[data-react-page]').forEach((page) => {
        if (page.querySelector('[data-react-root]')) mountReactPage(page);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
    start();
}
